import React, { useEffect, useState } from "react";
import { CalendarOff, Loader2, Trash2 } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { DayColumn, TimeAxis } from "./DayColumn";
import { cellsToWindows, windowsToCells, windowsLabel, weekdayKey, DAY_LABEL } from "./scheduleUtils";

const fmtDate = (iso) => new Date(`${iso}T00:00:00`).toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long" });

// Drawer for one date: pre-filled with the effective weekly schedule (or the existing override).
// Save → PUT override (windows or [] = day off); Remove → DELETE restores the weekly template for that date.
export function OverrideDrawer({ date, weekly, existing, onClose, onSave, onRemove, saving }) {
  const initial = existing ? existing.windows : (weekly[weekdayKey(date || "2026-01-05")] || []);
  const [cells, setCells] = useState(() => windowsToCells(initial));
  const [dayOff, setDayOff] = useState(() => !!existing && existing.windows.length === 0);
  const [reason, setReason] = useState(existing?.reason || "");
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    setCells(windowsToCells(initial)); setDayOff(!!existing && existing.windows.length === 0); setReason(existing?.reason || ""); setDirty(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date]);

  useEffect(() => {
    document.body.dataset.unsavedOverride = dirty ? "1" : "";
    return () => { document.body.dataset.unsavedOverride = ""; };
  }, [dirty]);

  const windows = dayOff ? [] : cellsToWindows(cells);
  const tryClose = () => {
    if (dirty && !window.confirm("You have unsaved changes for this date. Discard them?")) return;
    onClose();
  };

  return (
    <Sheet open={!!date} onOpenChange={(o) => { if (!o) tryClose(); }}>
      <SheetContent className="w-full sm:max-w-md overflow-y-auto" data-testid="override-drawer">
        {date && (
          <>
            <SheetHeader>
              <SheetTitle data-testid="override-title">{fmtDate(date)}</SheetTitle>
              <SheetDescription>
                {existing ? "This date has its own hours (override)." : `Using your weekly ${DAY_LABEL[weekdayKey(date)]} schedule.`} Changes here affect only this date — your weekly schedule stays as it is. Times in IST.
              </SheetDescription>
            </SheetHeader>

            <button type="button" onClick={() => { setDayOff((v) => !v); setDirty(true); }} data-testid="override-dayoff" data-on={dayOff ? "1" : "0"}
              className={`mt-4 w-full rounded-xl border px-3 py-2.5 text-sm font-bold inline-flex items-center justify-center gap-2 ${dayOff ? "bg-rose-50 border-rose-300 text-rose-700" : "bg-white border-slate-200 text-slate-700 hover:border-slate-400"}`}>
              <CalendarOff size={14} /> {dayOff ? "Marked as day off — tap to set hours instead" : "Mark as day off"}
            </button>

            {!dayOff && (
              <div className="mt-4 flex gap-3">
                <TimeAxis />
                <div className="flex-1 max-w-[140px]">
                  <DayColumn dayKey="override" label="Hours" cells={cells} onChange={(c) => { setCells(c); setDirty(true); }} />
                </div>
                <div className="flex-1 text-xs text-slate-500 pt-6">
                  <p className="font-bold text-slate-700 mb-1">Effective hours</p>
                  <p data-testid="override-summary">{windowsLabel(windows)}</p>
                </div>
              </div>
            )}

            <label className="block mt-4 text-xs font-bold text-slate-600">Reason (optional, private)
              <input value={reason} onChange={(e) => { setReason(e.target.value); setDirty(true); }} maxLength={140} placeholder="e.g. Court hearing" data-testid="override-reason"
                className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm font-normal" />
            </label>

            <p className="mt-3 text-[11px] text-slate-400">Existing paid consultations on this date stay booked — an override only changes what new clients can book.</p>

            <div className="mt-5 flex items-center gap-2">
              <Button onClick={() => onSave({ date, windows, reason })} disabled={saving || (!dirty && !!existing)} data-testid="override-save" className="flex-1 bg-[#0A2540] text-white hover:bg-[#0A2540]/90">
                {saving ? <Loader2 size={14} className="animate-spin" /> : existing ? "Update override" : dayOff ? "Save day off" : "Save hours for this date"}
              </Button>
              {existing && (
                <Button variant="outline" onClick={() => onRemove(date)} disabled={saving} data-testid="override-remove" className="text-rose-600 border-rose-200 hover:bg-rose-50">
                  <Trash2 size={14} className="mr-1" /> Remove
                </Button>
              )}
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
